import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Crown, Star, Gift } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (tierId) => {
    setLoading(tierId);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        tier_id: tierId,
        origin_url: window.location.origin,
        guest_email: null
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const tiers = [
    {
      id: "starter_wellness",
      name: "Starter Wellness",
      icon: Star,
      price: 29.99,
      description: "Perfect for those beginning their herbal wellness journey",
      features: [
        "3 Premium herbal tea blends (monthly rotation)",
        "Detailed brewing guide & health benefits card",
        "Access to our private wellness community",
        "10% discount on all future purchases"
      ],
      color: "bg-emerald-600"
    },
    {
      id: "family_favorites",
      name: "Family Favorites",
      icon: Crown,
      price: 49.99,
      description: "Our most popular box with exclusive JFRAI accessories",
      features: [
        "5 Premium herbal tea blends (monthly rotation)",
        "JFRAI Family Flavorful branded teacup",
        "JFRAI branded teaspoon",
        "Exclusive seasonal blend not available in store",
        "Brewing accessories & infuser",
        "15% discount on all future purchases",
        "Priority access to new products"
      ],
      color: "bg-orange-600",
      popular: true
    },
    {
      id: "ultimate_wellness",
      name: "Ultimate Wellness",
      icon: Gift,
      price: 79.99,
      description: "The complete JFRAI experience with oils, teas & more",
      features: [
        "8 Premium herbal tea blends (curated selection)",
        "2 Essential oils (rotating selection)",
        "1 Bath & body product",
        "JFRAI Family Flavorful branded tea set (cup, saucer, spoon)",
        "Premium wooden tea chest storage box",
        "Custom AI-powered wellness consultation",
        "20% discount on all future purchases",
        "Free shipping on all orders",
        "Early access to limited edition products"
      ],
      color: "bg-teal-600"
    }
  ];

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(204, 85, 0, 0.88), rgba(204, 85, 0, 0.88)), url(https://images.unsplash.com/photo-1563822249366-3efb23b8e0c9?q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Monthly Subscription Boxes
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Experience the magic of JFRAI delivered to your door every month. Choose the perfect wellness journey for you.
          </p>
        </div>

        {/* Subscription Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {tiers.map((tier, index) => {
            const Icon = tier.icon;
            return (
              <Card 
                key={index}
                data-testid={`subscription-tier-${tier.id}`}
                className={`relative ${tier.popular ? 'ring-4 ring-yellow-400 transform scale-105' : ''}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pt-8">
                  <div className={`${tier.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle
                    data-testid={`subscription-tier-name-${tier.id}`}
                    className="text-2xl"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {tier.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span data-testid={`subscription-tier-price-${tier.id}`} className="text-4xl font-bold text-gray-900">${tier.price}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <CardDescription className="mt-2 text-gray-600">{tier.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-800">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    data-testid={`subscription-tier-subscribe-${tier.id}`}
                    className={`w-full ${tier.color} hover:opacity-90 text-white`}
                    onClick={() => handleSubscribe(tier.id)}
                    disabled={loading === tier.id}
                  >
                    {loading === tier.id ? 'Processing...' : 'Subscribe Now'}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Benefits Section */}
        <Card className="bg-white/95 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-3xl text-center text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Subscribe?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-emerald-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Curated Selection</h3>
                <p className="text-sm text-gray-600">Expertly chosen teas and products tailored to the season</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Exclusive Products</h3>
                <p className="text-sm text-gray-600">Access to limited edition blends & branded accessories</p>
              </div>
              <div className="text-center">
                <div className="bg-emerald-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Crown className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Best Value</h3>
                <p className="text-sm text-gray-600">Save up to 20% compared to individual purchases</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Cancel Anytime</h3>
                <p className="text-sm text-gray-600">No long-term commitment, pause or cancel whenever you want</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
